import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, Check, Search, X, Radio } from 'lucide-react';
import "./TranscriptionDisplay.css"
import Divider from "@mui/material/Divider";
import { Circle } from 'lucide-react';

const TYPEWRITER_SPEED_MS = 18;
const BULK_CHAR_DELTA = 280;
const BULK_MIN_TOTAL = 260;

const normalizeLangCode = (lang) => String(lang || "").trim().toLowerCase();
const LANGUAGE_FLAG_COUNTRIES = {
    ar: "SA",
    bg: "BG",
    ca: "ES",
    cs: "CZ",
    da: "DK",
    de: "DE",
    el: "GR",
    en: "GB",
    es: "ES",
    et: "EE",
    fi: "FI",
    fr: "FR",
    he: "IL",
    hi: "IN",
    hr: "HR",
    hu: "HU",
    id: "ID",
    it: "IT",
    ja: "JP",
    ko: "KR",
    lt: "LT",
    lv: "LV",
    nl: "NL",
    no: "NO",
    pl: "PL",
    pt: "PT",
    ro: "RO",
    ru: "RU",
    sk: "SK",
    sl: "SI",
    sv: "SE",
    tr: "TR",
    uk: "UA",
    vi: "VN",
    zh: "CN",
};
const FRENCH_LANGUAGE_NAMES = {
    auto: "Détection auto",
    bg: "Bulgare",
    cs: "Tchèque",
    da: "Danois",
    de: "Allemand",
    el: "Grec",
    en: "Anglais",
    es: "Espagnol",
    et: "Estonien",
    fi: "Finnois",
    fr: "Français",
    he: "Hébreu",
    hi: "Hindi",
    hr: "Croate",
    hu: "Hongrois",
    id: "Indonésien",
    it: "Italien",
    ja: "Japonais",
    ko: "Coréen",
    lt: "Lituanien",
    lv: "Letton",
    nl: "Néerlandais",
    no: "Norvégien",
    pl: "Polonais",
    pt: "Portugais",
    ro: "Roumain",
    ru: "Russe",
    sk: "Slovaque",
    sl: "Slovène",
    sv: "Suédois",
    tr: "Turc",
    uk: "Ukrainien",
    vi: "Vietnamien",
    zh: "Chinois",
};
const FRENCH_REGION_NAMES = {
    gb: "Royaume-Uni",
    uk: "Royaume-Uni",
    us: "États-Unis",
};

const flagFromCountryCode = (countryCode) => {
    if (!/^[A-Z]{2}$/.test(countryCode || "")) return "🌐";
    return [...countryCode]
        .map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
        .join("");
};

const getLanguageParts = (code) => normalizeLangCode(code).split(/[-_]/);
const getPrimaryLang = (code) => getLanguageParts(code)[0] || "";

const getLanguageFlag = (code) => {
    const [language, region] = getLanguageParts(code);
    return flagFromCountryCode((region || LANGUAGE_FLAG_COUNTRIES[language] || "").toUpperCase());
};

const getFrenchLanguageLabel = (option = {}) => {
    const [language, region] = getLanguageParts(option.code);
    const baseLabel = FRENCH_LANGUAGE_NAMES[language] || option.label || option.code || "Langue";
    const regionLabel = region ? FRENCH_REGION_NAMES[region] || region.toUpperCase() : "";
    return regionLabel ? `${baseLabel} (${regionLabel})` : baseLabel;
};

// ── Dropdown custom ──────────────────────────────────────────────────────────

const LanguageDropdown = ({
    options,
    value,
    onChange,
    disabled,
    ariaLabel = "Langue",
    title = "Choisir une langue",
}) => {
    const [open, setOpen]   = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef           = useRef(null);
    const searchRef         = useRef(null);
    const listRef           = useRef(null);

    const selected = options.find((o) => o.code === value) || options[0];
    const selectedCode = selected?.code || value;

    const filtered = query.trim()
        ? options.filter(
              (o) =>
                  getFrenchLanguageLabel(o).toLowerCase().includes(query.toLowerCase()) ||
                  o.code.toLowerCase().includes(query.toLowerCase())
          )
        : options;

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setOpen((v) => !v);
        setQuery("");
    }, [disabled]);

    const handleSelect = useCallback((code) => {
        onChange(code);
        setOpen(false);
        setQuery("");
    }, [onChange]);

    /* Focus sur le champ de recherche à l'ouverture */
    useEffect(() => {
        if (open) {
            const id = requestAnimationFrame(() => searchRef.current?.focus());
            /* Scroll vers l'item actif */
            const idx = filtered.findIndex((o) => o.code === selectedCode);
            if (idx >= 0 && listRef.current) {
                const item = listRef.current.children[idx];
                item?.scrollIntoView({ block: "nearest" });
            }
            return () => cancelAnimationFrame(id);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Fermeture au clic extérieur */
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const triggerLabel = selected ? getFrenchLanguageLabel(selected) : "Choisir...";
    const triggerFlag = selected?.code === "auto" ? "🌐" : getLanguageFlag(selected?.code);

    return (
        <div className={`trs-dd-wrap ${open ? "trs-dd-wrap--open" : ""}`} ref={wrapRef}>
            {/* Liste flottante — au-dessus du trigger */}
            {open && (
                <div className="trs-dd-panel" role="listbox" aria-label={ariaLabel}>
                    <div className="trs-dd-search-row">
                        <input
                            ref={searchRef}
                            className="trs-dd-search"
                            type="text"
                            placeholder="ex: français"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Filtrer les langues"
                        />
                        <Search size={15} className="trs-dd-search-icon" aria-hidden="true" />
                    </div>
                    <ul className="trs-dd-list" ref={listRef}>
                        {filtered.map((o) => {
                            const active = o.code === selectedCode;
                            const flag = o.code === "auto" ? "🌐" : getLanguageFlag(o.code);
                            return (
                                <li
                                    key={o.code}
                                    role="option"
                                    aria-selected={active}
                                    className={`trs-dd-item ${active ? "trs-dd-item--active" : ""}`}
                                    onMouseDown={(e) => { e.preventDefault(); handleSelect(o.code); }}
                                >
                                    <span className="trs-dd-flag" aria-hidden="true">{flag}</span>
                                    <span className="trs-dd-item-label">{getFrenchLanguageLabel(o)}</span>
                                    {active && <Check size={13} className="trs-dd-item-check" />}
                                </li>
                            );
                        })}
                        {filtered.length === 0 && (
                            <li className="trs-dd-item-empty">Aucun résultat</li>
                        )}
                    </ul>
                </div>
            )}

            {/* Bouton trigger */}
            <button
                type="button"
                className={`trs-dd-trigger ${open ? "trs-dd-trigger--open" : ""}`}
                onClick={handleOpen}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`${ariaLabel} : ${triggerLabel}`}
                title={`${title} : ${triggerLabel}`}
            >
                <span className="trs-dd-flag" aria-hidden="true">{triggerFlag}</span>
                <span className="trs-dd-trigger-label">{triggerLabel}</span>
                <ChevronDown size={14} className={`trs-dd-trigger-chev ${open ? "trs-dd-trigger-chev--up" : ""}`} />
            </button>
        </div>
    );
};

// ── Composant principal ──────────────────────────────────────────────────────

const TranscriptionDisplay = ({
  transcribed_text,
  translated_text,
  target_lang,
  stt_source_lang,
  owner,
  TSstatus,
  isOwner,
  toggleTranscription,
  stt_language_options = [],
  display_language_options = [],
  display_lang = "",
  on_display_language_change,
  on_request_stt_languages,
  language_sync_notice = "",
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef(null);
    const scrollBoxRef = useRef(null);

    const displayLanguageOptions = display_language_options.length
        ? display_language_options
        : stt_language_options.filter((option) => option.code !== "auto");
    const selectedDisplayLang =
        display_lang ||
        target_lang ||
        displayLanguageOptions[0]?.code ||
        "";
    const selectedDisplayOption =
        displayLanguageOptions.find((option) => option.code === selectedDisplayLang) ||
        displayLanguageOptions.find((option) => getPrimaryLang(option.code) === getPrimaryLang(selectedDisplayLang)) ||
        null;
    const selectedDisplayLabel = selectedDisplayOption
        ? getFrenchLanguageLabel(selectedDisplayOption)
        : selectedDisplayLang
            ? selectedDisplayLang.toUpperCase()
            : "Indisponible";
    const sourceMatchesDisplay =
        getPrimaryLang(stt_source_lang) !== "" &&
        getPrimaryLang(stt_source_lang) === getPrimaryLang(selectedDisplayLang);
    const sourceBaselineRef = useRef(0);
    const baselineLangRef = useRef(selectedDisplayLang);

    if (baselineLangRef.current !== selectedDisplayLang) {
        if (sourceMatchesDisplay) {
            sourceBaselineRef.current = (transcribed_text || "").length;
        }
        baselineLangRef.current = selectedDisplayLang;
    }

    const translationMatchesDisplay =
        normalizeLangCode(target_lang) !== "" &&
        normalizeLangCode(target_lang) === normalizeLangCode(selectedDisplayLang);
    const sourceText = sourceMatchesDisplay
        ? (transcribed_text || "").slice(sourceBaselineRef.current).trimStart()
        : "";
    const activeText = sourceMatchesDisplay
        ? sourceText
        : translationMatchesDisplay
            ? (translated_text || "")
            : "";

    const [displayed, setDisplayed] = useState("");
    const timerRef = useRef(null);
    const targetRef = useRef("");
    const prevDisplayLangRef = useRef(selectedDisplayLang);
    const lastActiveTextRef = useRef("");
    const pendingScrollBottomRef = useRef(false);

    const scrollTranscriptToBottom = () => {
        const scrollBox = scrollBoxRef.current;
        const content = contentRef.current;
        if (!scrollBox || !content) return;
        if (content.scrollHeight > scrollBox.clientHeight) {
            scrollBox.scrollTop = scrollBox.scrollHeight;
        }
    };

    useEffect(() => {
        const newText = activeText || "";
        targetRef.current = newText;

        const modeChanged = prevDisplayLangRef.current !== selectedDisplayLang;
        prevDisplayLangRef.current = selectedDisplayLang;

        clearInterval(timerRef.current);

        if (modeChanged) {
            lastActiveTextRef.current = newText;
            setDisplayed(newText);
            targetRef.current = newText;
            pendingScrollBottomRef.current = true;
            return;
        }

        const prevText = lastActiveTextRef.current;
        const prevLen = prevText.length;
        const deltaLen = newText.length - prevLen;
        const isBulk =
            newText.length >= BULK_MIN_TOTAL &&
            (deltaLen >= BULK_CHAR_DELTA || (prevLen < 40 && newText.length >= BULK_MIN_TOTAL));

        lastActiveTextRef.current = newText;

        if (isBulk) {
            setDisplayed(newText);
            pendingScrollBottomRef.current = true;
            return;
        }

        setDisplayed((prev) => {
            if (newText.startsWith(prev)) return prev;
            return newText;
        });

        timerRef.current = setInterval(() => {
            setDisplayed((prev) => {
                const target = targetRef.current;
                if (prev.length >= target.length) {
                    clearInterval(timerRef.current);
                    return target;
                }
                return target.slice(0, prev.length + 1);
            });
        }, TYPEWRITER_SPEED_MS);

        return () => clearInterval(timerRef.current);
    }, [activeText, selectedDisplayLang]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!pendingScrollBottomRef.current) return;
        pendingScrollBottomRef.current = false;
        const id = requestAnimationFrame(() => {
            scrollTranscriptToBottom();
            requestAnimationFrame(scrollTranscriptToBottom);
        });
        return () => cancelAnimationFrame(id);
    }, [displayed, selectedDisplayLang]);

    useEffect(() => {
        if (!isExpanded) return;
        const id = requestAnimationFrame(() => { scrollTranscriptToBottom(); });
        return () => cancelAnimationFrame(id);
    }, [isExpanded]);

    useEffect(() => {
        if (!isExpanded) return;
        const scrollBox = scrollBoxRef.current;
        const content = contentRef.current;
        if (!scrollBox || !content) return;
        const scrollToBottom = () => {
            if (content.scrollHeight > scrollBox.clientHeight)
                scrollBox.scrollTop = scrollBox.scrollHeight;
        };
        const ro = new ResizeObserver(scrollToBottom);
        ro.observe(content);
        return () => ro.disconnect();
    }, [isExpanded]);

    const langSelectsDisabled = TSstatus === "pending";

    const handleToggle = () => setIsExpanded((v) => !v);

    return (
        <div className="transcription-section">
            {TSstatus === "active" && isOwner && isExpanded && typeof toggleTranscription === "function" && (
                <button
                    type="button"
                    className="trs-close-btn"
                    onClick={() => toggleTranscription()}
                    aria-label="Arrêter la transcription"
                    title="Arrêter la transcription"
                >
                    <X size={18} strokeWidth={2.5} />
                </button>
            )}
            <div className="trs-relative">
                {isExpanded && (
                    <div className="trs-indicator">
                        <div className="ts-owner">{owner}</div>
                    </div>
                )}
                <div ref={scrollBoxRef} className={`trs-display-box ${isExpanded ? "trs-expanded" : "trs-collapsed"}`}>
                    <div ref={contentRef} className={`trs-relative ${isExpanded ? "" : "trs-h"}`}>
                        <p className="trs-transcript" style={{ whiteSpace: "pre-wrap" }}>
                            {displayed}
                            {displayed.length < activeText.length && (
                                <span className="trs-cursor">▋</span>
                            )}
                        </p>
                    </div>
                </div>

                {isExpanded && (
                    <Divider sx={{ marginBottom: "8px", backgroundColor: "var(--peoplmap-primary)" }} />
                )}

                <div className="ts-indicator-footer">
                    <div className={`ts-indicator-stack ${TSstatus === "active" ? "" : "ts-muted"}`}>
                        <div className="ts-indicator-text">
                            <Radio className="ts-indicator-icon" size={24} aria-hidden="true" />
                            <span>
                                {TSstatus === "active"
                                    ? "Transcription en cours"
                                    : TSstatus === "pending"
                                    ? "Transcription en attente..."
                                    : "Transcription inactive"}
                            </span>
                        </div>
                        <div className="trs-reading-lang" title="Langue dans laquelle vous lisez la transcription">
                            Lecture en : <strong>{selectedDisplayLabel}</strong>
                        </div>
                        {language_sync_notice && (
                            <div className="trs-lang-sync-note">{language_sync_notice}</div>
                        )}
                        {displayLanguageOptions.length === 0 && (
                            <div className="trs-lang-sync-note">Langues indisponibles pour le moment</div>
                        )}
                    </div>
                    {displayLanguageOptions.length === 0 ? (
                        <button
                            type="button"
                            className="trs-lang-retry-icon-btn"
                            onClick={() => on_request_stt_languages?.()}
                            title="Langues indisponibles pour le moment. Recharger les langues d'affichage"
                            aria-label="Langues indisponibles pour le moment. Recharger les langues d'affichage"
                        >
                            🌐
                        </button>
                    ) : (
                        <LanguageDropdown
                            options={displayLanguageOptions}
                            value={selectedDisplayLang || displayLanguageOptions[0]?.code || ""}
                            onChange={(code) => on_display_language_change?.(code)}
                            disabled={langSelectsDisabled}
                            ariaLabel="Langue de lecture"
                            title="Choisir la langue dans laquelle lire la transcription"
                        />
                    )}
                    <button onClick={handleToggle} className="trs-expand-btn">
                        <ChevronUp className={`trs-chev ${isExpanded ? "trs-chev-expanded" : ""}`} />
                    </button>
                </div>

            </div>
        </div>
        
    );
};

export default TranscriptionDisplay;
